import { ContactHistory } from '../case/model/contact-history';
import { Component, OnInit, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { ContactHistoryService } from 'src/app/core/service/contact-history.service';
import { NGXLogger } from 'ngx-logger';
import moment from 'moment';
import { SettingService } from '../core/service/setting.service';
import { Dictionary } from 'src/app/core/model/dictionary';
import { DictionaryService } from '../core/service/dictionary.service';
import { sortBy } from 'lodash';
import { IntlService } from '@progress/kendo-angular-intl';
import { forkJoin } from 'rxjs';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { UserService } from 'src/app/core/service/user.service';
import { TranslateService } from '@ngx-translate/core';
import { MemberService } from 'src/app/core/service/member.service';

@Component({
  selector: 'app-contact-history',
  templateUrl: './contact-history.component.html',
  styleUrls: ['./contact-history.component.scss']
})
export class ContactHistoryComponent implements OnInit, OnChanges
{
  @Input() public contactHistoryDialogOpened: boolean;
  @Input() public memberId: number;
  @Input() public taskId: string;
  @Input() public outcome: string;
  @Input() public contact: any;
  @Input() public contactCodeset: number;
  @Input() public initiativeCodeset: number;
  @Input() public areaCodeset: number;

  @Output() public added = new EventEmitter<any>();
  @Output() public cancelling = new EventEmitter();

  public contactHistory: ContactHistory = new ContactHistory();

  public minCO: Date = moment().subtract('1', 'year').toDate();
  public maxCO: Date = moment().toDate();
  public showActivities = false;
  public showConsent = false;
  public showEntity = false;
  public showIntensity = false;
  public showMethod = false;
  public showMood = false;
  public requiredActivities = false;
  public requiredConsent = false;
  public requiredEntity = false;
  public requiredIntensity = false;
  public requiredMethod = false;
  public requiredMood = false;
  public contactsContactedOnEnabled = false;
  public virtualContactActitivies: any = { itemHeight: 30 };
  public contactActivitiesFiltered: Dictionary[] = [];
  public virtualContactAreas: any = { itemHeight: 30 };
  public contactAreasFiltered: Dictionary[] = [];

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  /**
   * Angular form group for the Contact History.
   */
  public contactHistoryForm: any;
  public contactMethods: Dictionary[] = [];
  public defaultContactActivities: Dictionary[] = [];
  public allContactActivities: Dictionary[] = [];
  public allContactAreas: Dictionary[] = [];
  public contactInitiatives: Dictionary[] = [];
  public contactConsents: Dictionary[] = [];
  public contactEntities: Dictionary[] = [];
  public contactIntensities: Dictionary[] = [];
  public patientMoods: Array<{ text: string; value: string; icon: string }>;
  public patientLabel = {};
  public systemGenerated = false;
  public recordContactFieldsSetting: string;

  constructor(
    private contactHistoryService: ContactHistoryService,
    private messageHandlerService: MessageHandlerService,
    private dictionaryService: DictionaryService,
    private fb: UntypedFormBuilder,
    private logger: NGXLogger,
    private settingService: SettingService,
    public intl: IntlService,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private translateService: TranslateService,
    private memberService: MemberService,
  ) { }

  public get contactMethod(): UntypedFormControl
  {
    return this.contactHistoryForm.get('contactMethod');
  }

  public get contactActivities(): UntypedFormControl
  {
    return this.contactHistoryForm.get('contactActivities');
  }

  public get contactAreas(): UntypedFormControl
  {
    return this.contactHistoryForm.get('contactAreas');
  }

  public get contactEntity(): UntypedFormControl
  {
    return this.contactHistoryForm.get('contactEntity');
  }

  public get contactIntensity(): UntypedFormControl
  {
    return this.contactHistoryForm.get('contactIntensity');
  }

  public get contactConsent(): UntypedFormControl
  {
    return this.contactHistoryForm.get('contactConsent');
  }

  public get contactInitiative(): UntypedFormControl
  {
    return this.contactHistoryForm.get('contactInitiative');
  }

  public get patientMood(): UntypedFormControl
  {
    return this.contactHistoryForm.get('patientMood');
  }

  public get contactedOn(): UntypedFormControl
  {
    return this.contactHistoryForm.get('contactedOn');
  }

  public get comments(): UntypedFormControl
  {
    return this.contactHistoryForm.get('comments');
  }

  public ngOnInit(): void
  {
    this.maxCO.setMinutes(this.maxCO.getMinutes() + 1);
    this.patientMoods = this.contactHistoryService.patientMoods;

    forkJoin([
      this.dictionaryService.getCachedContactTypes(),
      this.settingService.getCachedSessionSettings(),
      this.dictionaryService.getCachedContactActivities(),
      this.dictionaryService.getCachedContactEntities(),
      this.dictionaryService.getCachedContactIntensities(),
      this.dictionaryService.getCachedConsentResponses()
    ]).subscribe({
      next: (responseList: any) =>
      {
        this.contactMethods = sortBy(responseList[0], ['id']);
        this.recordContactFieldsSetting = responseList[1].recordContactFields;

        this.showActivities = responseList[1].recordContactFields.includes(this.translateService.instant('ACTIVITIES').toUpperCase()) ? true : false;
        this.showConsent = responseList[1].recordContactFields.includes(this.translateService.instant('CONSENT').toUpperCase()) ? true : false;
        this.showEntity = responseList[1].recordContactFields.includes(this.translateService.instant('ENTITY').toUpperCase()) ? true : false;
        this.showIntensity = responseList[1].recordContactFields.includes(this.translateService.instant('INTENSITY').toUpperCase()) ? true : false;
        this.showMethod = responseList[1].recordContactFields.includes(this.translateService.instant('METHOD').toUpperCase()) ? true : false;
        this.showMood = responseList[1].recordContactFields.includes(this.translateService.instant('SENTIMENT').toUpperCase()) ? true : false;

        this.requiredActivities = this.memberService.getFieldRequiredValue(this.recordContactFieldsSetting, this.translateService.instant('ACTIVITIES').toUpperCase());
        this.requiredConsent = this.memberService.getFieldRequiredValue(this.recordContactFieldsSetting, this.translateService.instant('CONSENT').toUpperCase());
        this.requiredEntity = this.memberService.getFieldRequiredValue(this.recordContactFieldsSetting, this.translateService.instant('ENTITY').toUpperCase());
        this.requiredIntensity = this.memberService.getFieldRequiredValue(this.recordContactFieldsSetting, this.translateService.instant('INTENSITY').toUpperCase());
        this.requiredMethod = this.memberService.getFieldRequiredValue(this.recordContactFieldsSetting, this.translateService.instant('METHOD').toUpperCase());
        this.requiredMood = this.memberService.getFieldRequiredValue(this.recordContactFieldsSetting, this.translateService.instant('SENTIMENT').toUpperCase());

        this.contactsContactedOnEnabled = (responseList[1].contactsContactedOnRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, responseList[1].contactsContactedOnRoles) : false;
        this.patientLabel = { patientLabel: responseList[1].patientLabel };
        this.defaultContactActivities = sortBy(responseList[2], ['id']);
        this.contactEntities = sortBy(responseList[3], ['id']);
        this.contactIntensities = sortBy(responseList[4], ['id']);
        this.contactConsents = sortBy(responseList[5], ['id']);
      }
    });
  }

  public ngOnChanges(): void
  {
    this.initContactHistory();
    this.buildForm();
  }

  /**
   * Form submission event handler.
   */
  public createContactHistory(): void
  {
    this.contactHistory.method = this.contactHistoryForm.controls.contactMethod.value;
    this.contactHistory.entity = this.contactHistoryForm.controls.contactEntity.value;
    this.contactHistory.intensity = this.contactHistoryForm.controls.contactIntensity.value;
    this.contactHistory.activities = this.contactHistoryForm.controls.contactActivities.value;
    this.contactHistory.consent = this.contactHistoryForm.controls.contactConsent.value;
    this.contactHistory.initiative = this.contactHistoryForm.controls.contactInitiative.value;
    this.contactHistory.areas = this.contactHistoryForm.controls.contactAreas.value;

    if (this.showMood)
    {
      this.contactHistory.sentiment = this.contactHistoryForm.controls.patientMood.value;
    }
    else
    {
      delete this.contactHistory.sentiment;
    }

    this.contactHistory.comments = this.contactHistoryForm.controls.comments.value;
    this.contactHistory.contactedOn = moment.utc(this.contactHistoryForm.controls.contactedOn.value).toDate();
    this.contactHistory.memberId = this.memberId;
    this.contactHistory.taskHistory = (this.taskId ? { id: this.taskId } : null);

    this.contactHistoryService.createContactHistory(this.contactHistory).subscribe({
      next: () =>
      {
        this.added.emit(this.outcome);
        this.contactHistoryForm.reset();
        this.contactHistoryDialogOpened = false;
        this.messageHandlerService.displaySuccess('CONTACT-HISTORY-SAVED-SUCCESSFULLY');
      },
      error: error =>
      {
        this.logger.error(error);
        this.messageHandlerService.displayError('CONTACT-HISTORY-SAVE-ERROR');
      }
    });
  }

  public cancelContactHistory(): void
  {
    // Close filter dialog
    this.contactHistoryForm.reset();
    this.contactHistoryDialogOpened = false;
    this.cancelling.emit();
  }

  public hasTask(): boolean
  {
    if (this.taskId)
    {
      return true;
    } else
    {
      return false;
    }
  }

  public selectPatientMood(mood: string): void
  {
    this.patientMood.setValue(mood);
    this.contactHistoryForm.markAsDirty();
  }

  /**
   * Filters contact activities based on user input.
   */
  public filterContactActivities(filter: string): void
  {
    this.searchTerm = filter;
    this.contactActivitiesFiltered = this.allContactActivities
      .filter(user => user.displayName.toLowerCase().includes(filter.toLowerCase()));
  }

  /**
   * Filters contact focus areas based on user input.
   */
  public filterContactAreas(filter: string): void
  {
    this.searchTerm = filter;
    this.contactAreasFiltered = this.allContactAreas
      .filter(user => user.displayName.toLowerCase().includes(filter.toLowerCase()));
  }

  private initContactHistory(): void
  {
    if (this.contact !== undefined && this.contact !== null)
    {
      this.contactHistory.id = this.contact.id;
      this.contactHistory.method = this.contact.method;
      this.contactHistory.entity = this.contact.entity;
      this.contactHistory.activities = this.contact.activities;
      this.contactHistory.areas = this.contact.areas;
      this.contactHistory.consent = this.contact.consent;
      this.contactHistory.intensity = this.contact.intensity;
      this.contactHistory.initiative = this.contact.initiative;
      this.contactHistory.sentiment = this.contact.sentiment;
      this.contactHistory.contactedOn = this.contact.contactedOn;
      this.contactHistory.comments = this.contact.comments;
      this.contactHistory.systemGenerated = this.contact.systemGenerated;
    }
    else
    {
      this.contactHistory = new ContactHistory();
    }
  }

  private buildForm(): void
  {
    if (this.contact === undefined)
    {
      this.initContactHistory();
    }

    const activitiesValidator = this.showActivities ? this.requiredActivities : [];
    const consentValidator = this.showConsent ? this.requiredConsent : [];
    const entityValidator = this.showEntity ? this.requiredEntity : [];
    const intensityValidator = this.showIntensity ? this.requiredIntensity : [];
    const methodValidator = this.showMethod ? this.requiredMethod : [];
    const moodValidator = this.showMood ? this.requiredMood : [];
    this.systemGenerated = this.contactHistory.systemGenerated;

    // Load proper codeset for the Activity drop-down
    this.allContactActivities = this.defaultContactActivities;
    this.filterContactActivities('');
    if (this.contactCodeset !== undefined || (this.contactHistory.activities && this.contactHistory.activities.length > 0))
    {
      const codesetId = this.contactCodeset || this.contactHistory.activities[0].codeSetId;
      this.dictionaryService.getCodesByCodeSetId(codesetId).subscribe((data) =>
      {
        this.allContactActivities = data;
        this.filterContactActivities('');
      });
    }

    // Load proper codeset for the Activity drop-down
    this.allContactAreas = [];
    this.filterContactAreas('');
    if (this.areaCodeset !== undefined || (this.contactHistory.areas && this.contactHistory.areas.length > 0))
    {
      const codesetId = this.areaCodeset || this.contactHistory.areas[0].codeSetId;
      this.dictionaryService.getCodesByCodeSetId(codesetId).subscribe((data) =>
      {
        this.allContactAreas = data;
        this.filterContactAreas('');
      });
    }

    // Load proper codeset for the Initiative drop-down
    this.contactInitiatives = [];
    if (this.initiativeCodeset !== undefined || this.contactHistory.initiative?.codeSetId)
    {
      const codesetId = this.initiativeCodeset || this.contactHistory.initiative.codeSetId;
      this.dictionaryService.getCodesByCodeSetId(codesetId).subscribe((data) =>
      {
        this.contactInitiatives = data;
      });
    }

    this.contactHistoryForm = this.fb.group({
      id: [this.contactHistory.id],
      contactMethod: [{ value: this.contactHistory.method, disabled: this.systemGenerated }, { validators: methodValidator === true ? [Validators.required] : [] }],
      contactEntity: [{ value: this.contactHistory.entity, disabled: this.systemGenerated }, { validators: entityValidator === true ? [Validators.required] : [] }],
      contactIntensity: [{ value: this.contactHistory.intensity, disabled: this.systemGenerated }, { validators: intensityValidator === true ? [Validators.required] : [] }],
      contactActivities: [{ value: this.contactHistory.activities, disabled: this.systemGenerated }, { validators: activitiesValidator === true ? [Validators.required] : [] }],
      contactConsent: [{ value: this.contactHistory.consent, disabled: this.systemGenerated }, { validators: consentValidator === true ? [Validators.required] : [] }],
      contactInitiative: [{ value: this.contactHistory.initiative, disabled: this.systemGenerated },
      {
        validators:
          this.initiativeCodeset !== undefined || this.contactHistory.initiative ? [Validators.required] : []
      }],
      contactAreas: [{ value: this.contactHistory.areas, disabled: this.systemGenerated },
      {
        validators:
          this.areaCodeset !== undefined || this.contactHistory.areas.length > 0 ? [Validators.required] : []
      }],
      patientMood: [{ value: this.contactHistory.sentiment, disabled: this.systemGenerated }, { validators: moodValidator === true ? [Validators.required] : [] }],
      contactedOn: [{ value: this.contactHistory.contactedOn, disabled: !this.contactsContactedOnEnabled }, { validators: [Validators.required] }],
      comments: { value: this.contactHistory.comments, disabled: false }
    });
  }
}
